import React from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text, Icon } from "@xsolla/xui-primitives";
import { useResolvedTheme, type ThemeOverrideProps } from "@xsolla/xui-core";
import { Badge } from "@xsolla/xui-badge";
import { User } from "@xsolla/xui-icons-base";

export interface AvatarProps extends ThemeOverrideProps {
  /** Image source URL */
  src?: string;
  /** Icon component to display when no image is provided */
  icon?: React.ReactNode;
  /** Text/Initials to display when no image or icon is provided */
  text?: string;
  /** Size of the avatar */
  size?: "xl" | "lg" | "md" | "sm" | "xs" | "xxs";
  /** If true, the avatar will be square with small border radius. If false, it will be a circle. */
  square?: boolean;
  /** Visual tone — 'mono' (default) or 'brand'. */
  tone?: "mono" | "brand";
  /**
   * Whether to render the avatar's outer border (stroke). Defaults to `true`,
   * preserving the existing bordered look. Set to `false` to remove the border
   * entirely — useful on surfaces that already provide separation, or in dense
   * layouts where the border adds visual noise.
   */
  stroke?: boolean;
  /** Whether to show an alert badge */
  badge?: boolean;
  /** Number or text to display in the badge notification */
  badgeCount?: React.ReactNode;
  /** Icon to display in the badge */
  badgeIcon?: React.ReactNode;
  /**
   * Background colour override. Intended for internal use by AvatarGroup to
   * drive its `avatarBackgroundMode` cycling; not part of the public API.
   * @internal
   */
  backgroundColor?: string;
  /** Disable hover effect. Used internally by AvatarGroup. */
  disableHover?: boolean;
  /** Accessible label for the avatar. Recommended for screen readers. */
  "aria-label"?: string;
  /** Alternative text for the avatar image */
  alt?: string;
  /** Click handler for the avatar */
  onClick?: () => void;
  /** Color tone for the badge */
  badgeTone?:
    | "primary"
    | "secondary"
    | "brand"
    | "brandExtra"
    | "success"
    | "warning"
    | "alert"
    | "neutral";
  testID?: string;
}

export const Avatar: React.FC<AvatarProps> = ({
  src,
  icon,
  text,
  size = "xl",
  square = false,
  tone = "mono",
  stroke = true,
  badge = false,
  badgeCount,
  badgeIcon,
  badgeTone = "alert",
  backgroundColor,
  disableHover = true,
  "aria-label": ariaLabel,
  alt,
  onClick,
  testID,
  themeMode,
  themeProductContext,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });

  const [hasImageError, setHasImageError] = React.useState(false);

  React.useEffect(() => {
    setHasImageError(false);
  }, [src]);

  const useImage = !!src && !hasImageError;

  const sizeStyles = theme.sizing.avatar(size);

  const toneBackground =
    tone === "brand"
      ? theme.colors.background.brand.primary
      : theme.colors.overlay.mono;
  const toneHoverBackground =
    tone === "brand"
      ? theme.colors.background.brand.secondary
      : theme.colors.overlay.monoHover;
  const toneContent =
    tone === "brand"
      ? theme.colors.content.on.brand
      : theme.colors.content.primary;

  const borderRadius = square
    ? sizeStyles.borderRadiusSquare
    : sizeStyles.borderRadiusCircle;

  const countSizeMap = {
    xl: "xl",
    lg: "xl",
    md: "lg",
    sm: "lg",
    xs: "sm",
    xxs: "xs",
  } as const;
  const dotSizeMap = {
    xl: "md",
    lg: "md",
    md: "sm",
    sm: "sm",
    xs: "sm",
    xxs: "xs",
  } as const;
  const hasBadgeContent = !!(badgeCount || badgeIcon);
  const badgeSize = hasBadgeContent ? countSizeMap[size] : dotSizeMap[size];
  // Badge sizes sm/xs are dot-only — content is dropped, so position as a dot.
  const rendersAsDot = badgeSize === "sm" || badgeSize === "xs";

  // Badge offset differs for circle vs square, and for dot vs count
  // Dot badge (8px) needs different offset per avatar size
  const getDotOffset = () => {
    if (square) {
      return size === "xxs" ? { right: -1, top: -1 } : { right: -3, top: -3 };
    }
    // Circle - position dot on the edge based on avatar size
    const circleOffsets = {
      xl: { right: 2, top: 2 },
      lg: { right: 2, top: 2 },
      md: { right: 2, top: 2 },
      sm: { right: 2, top: 2 },
      xs: { right: 0, top: 0 },
      xxs: { right: 1, top: 1 },
    };
    return circleOffsets[size];
  };

  const badgeOffset =
    hasBadgeContent && !rendersAsDot
      ? square
        ? sizeStyles.badgeOffsetSquare
        : sizeStyles.badgeOffsetCircle
      : getDotOffset();

  const displayBadgeCount = badgeCount;

  return (
    <Box
      testID={testID}
      width={sizeStyles.size}
      height={sizeStyles.size}
      position="relative"
      {...(!useImage && { role: "img", "aria-label": ariaLabel })}
      {...(onClick && {
        onPress: onClick,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        },
        cursor: "pointer",
        role: "button",
        tabIndex: 0,
      })}
    >
      <Box
        width={sizeStyles.size}
        height={sizeStyles.size}
        borderRadius={borderRadius}
        backgroundColor={backgroundColor || toneBackground}
        alignItems="center"
        justifyContent="center"
        position="relative"
        overflow="hidden"
        borderWidth={stroke ? theme.shape.avatar[size].borderWidth : 0}
        borderColor={stroke ? theme.colors.border.secondary : undefined}
        {...(!disableHover &&
          !useImage && {
            hoverStyle: {
              backgroundColor: toneHoverBackground,
            },
          })}
      >
        {useImage ? (
          <Box
            as="img"
            src={src}
            alt={alt || ariaLabel || ""}
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            borderRadius={borderRadius}
            onError={() => setHasImageError(true)}
          />
        ) : icon ? (
          <Icon size={sizeStyles.iconSize} color={toneContent}>
            {icon}
          </Icon>
        ) : text ? (
          <Text
            fontSize={sizeStyles.fontSize}
            color={toneContent}
            fontWeight="400"
            letterSpacing={0.4}
          >
            {text}
          </Text>
        ) : (
          <User
            variant="solid"
            size={sizeStyles.iconSize}
            color={toneContent}
          />
        )}
      </Box>

      {badge && (
        <Box
          position="absolute"
          right={badgeOffset.right}
          top={badgeOffset.top}
        >
          <Badge
            size={badgeSize}
            tone={badgeTone}
            icon={badgeIcon}
            showStroke={size !== "xxs"}
            aria-label={
              displayBadgeCount
                ? `${displayBadgeCount} notifications`
                : "Notification"
            }
          >
            {displayBadgeCount}
          </Badge>
        </Box>
      )}
    </Box>
  );
};
